import { Pipe, PipeTransform } from '@angular/core';
import { DateTime } from 'luxon';

@Pipe({
  name: 'sortBy',
  standalone: true,
})
export class SortByPipe implements PipeTransform {
  /**
   * Сортирует массив с гибкой настройкой положения null/undefined
   * @param value - Входной массив
   * @param property - Свойство для сортировки. Если не указано, сортирует примитивы (числа, строки).
   * @param direction - Направление ('asc'|'desc'). По умолчанию 'asc'.
   * @param nullsPosition - Положение null/undefined ('first'|'last'). По умолчанию 'last'.
   * @param latinFirst - Флаг, указывающий, следует ли сортировать строки с латиницей перед кириллицей. По умолчанию false.
   * @param skipItems - Массив элементов, которые нужно исключить из сортировки и добавить в начало. По умолчанию пустой массив.
   * @returns Отсортированный массив
   */
  transform(
    value: any[],
    property?: string,
    direction: 'asc' | 'desc' = 'asc',
    nullsPosition: 'first' | 'last' = 'last',
    latinFirst = false, // Новый параметр
    skipItems: any[] = [],
  ): any[] {
    if (!Array.isArray(value)) {
      return value;
    }

    const result = [...value];

    // Убираем элементы, которые нужно пропустить, и добавляем их в начало
    const skippedItems = result.filter((item) =>
      skipItems.some((skip) => this.isEqual(item, skip)),
    );
    const filteredResult = result.filter(
      (item) => !skipItems.some((skip) => this.isEqual(item, skip)),
    );

    // Если property не указано, сортируем примитивы (числа, строки)
    if (!property) {
      return [
        ...skippedItems,
        ...filteredResult.sort((a, b) => this.compareValues(a, b, direction, nullsPosition)),
      ];
    }

    // Сортировка объектов по свойству
    return [
      ...skippedItems,
      ...filteredResult.sort((a, b) => {
        const aValue = this.getNestedProperty(a, property);
        const bValue = this.getNestedProperty(b, property);

        return this.compareValues(aValue, bValue, direction, nullsPosition, latinFirst);
      }),
    ];
  }

  private compareValues(
    a: any,
    b: any,
    direction: 'asc' | 'desc',
    nullsPosition: 'first' | 'last',
    latinFirst = false,
  ): number {
    // Обработка null/undefined
    if (a == null && b == null) return 0;
    if (a == null) return nullsPosition === 'first' ? -1 : 1;
    if (b == null) return nullsPosition === 'first' ? 1 : -1;

    // Парсинг и сравнение дат ДО проверки строк
    const aDate = this.tryParseDate(a);
    const bDate = this.tryParseDate(b);

    if (aDate !== null && bDate !== null) {
      // При asc — свежие даты сначала
      return direction === 'asc' ? bDate - aDate : aDate - bDate;
    }

    // Сравнение булевых значений
    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return direction === 'asc' ? (a === b ? 0 : a ? -1 : 1) : a === b ? 0 : a ? 1 : -1;
    }

    // Сравнение строк с учётом латиницы и кириллицы
    if (typeof a === 'string' && typeof b === 'string') {
      if (latinFirst) {
        const isALatin = /^[a-zA-Z]/.test(a);
        const isBLatin = /^[a-zA-Z]/.test(b);

        if (isALatin && !isBLatin) return -1;
        if (!isALatin && isBLatin) return 1;
      }

      return direction === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
    }
    // Стандартное сравнение
    return direction === 'asc' ? (a < b ? -1 : a > b ? 1 : 0) : a > b ? -1 : a < b ? 1 : 0;
  }

  private tryParseDate(value: any): number | null {
    if (typeof value !== 'string') return null;
    const date = DateTime.fromISO(value);
    return date.isValid ? date.toMillis() : null;
  }

  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && typeof current === 'object' ? current[key] : undefined;
    }, obj);
  }

  private isEqual(item1: any, item2: any): boolean {
    return JSON.stringify(item1) === JSON.stringify(item2);
  }
}
